import { Drawer } from '@base-ui/react/drawer';
import { useState } from 'react';
import { dateFormat, decimalFormat, hasPhotoLocation, numberFormat } from '../../domain/trail';
import type { CalendarDay, RestDay } from '../../domain/trail';
import type { Activity, Photo } from '../../schemas';
import {
  CloseButton,
  DayStats,
  DrawerContent,
  DrawerDate,
  DrawerHandle,
  DrawerHeader,
  DrawerNavigation,
  DrawerNavigationButton,
  DrawerPopup,
  DrawerTitle,
  DrawerViewport,
  PhotoSection,
  Section,
  SectionHeading,
} from '../../styles';
import { DayPhotos } from '../photos/DayPhotos';
import { ElevationChart } from './ElevationChart';

const mobileDrawerSnapPoints: Drawer.Root.SnapPoint[] = ['13.5rem', 1];

export function ActivityDrawer({
  activity,
  restDay,
  photos,
  journeyPhotos,
  previousDay,
  nextDay,
  spotlightPhotoId,
  isMobile,
  onClose,
  onNavigate,
  onSpotlightDayChange,
  onSpotlightClose,
  onPhotoMarkerHighlightChange,
  onElevationLocationChange,
}: {
  activity: Activity | null;
  restDay: RestDay | null;
  photos: Photo[];
  journeyPhotos: Photo[];
  previousDay: CalendarDay | null;
  nextDay: CalendarDay | null;
  spotlightPhotoId: string | null;
  isMobile: boolean;
  onClose: () => void;
  onNavigate: (day: CalendarDay) => void;
  onSpotlightDayChange: (date: string) => void;
  onSpotlightClose: () => void;
  onPhotoMarkerHighlightChange: (photoId: string | null) => void;
  onElevationLocationChange: (coordinate: [number, number] | null) => void;
}) {
  const [highlightedPhotoLocation, setHighlightedPhotoLocation] = useState<[number, number] | null>(null);
  const [mobileSnapPoint, setMobileSnapPoint] = useState<Drawer.Root.SnapPoint | null>(1);
  const dayDate = activity?.date ?? restDay?.date ?? null;
  const dayTitle = activity?.title ?? restDay?.location ?? 'Walk day';

  const handlePhotoHighlightChange = (photo: Photo | null) => {
    const coordinate = photo && hasPhotoLocation(photo) ? ([photo.lon, photo.lat] as [number, number]) : null;
    setHighlightedPhotoLocation(coordinate);
    onPhotoMarkerHighlightChange(photo?.id ?? null);
    onElevationLocationChange(coordinate);
  };

  return (
    <Drawer.Root
      open={dayDate !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      swipeDirection={isMobile ? 'down' : 'right'}
      snapPoints={isMobile ? mobileDrawerSnapPoints : undefined}
      snapPoint={isMobile ? mobileSnapPoint : undefined}
      onSnapPointChange={(snapPoint) => {
        if (isMobile) setMobileSnapPoint(snapPoint);
      }}
      snapToSequentialPoints
      modal={false}
      disablePointerDismissal
    >
      <Drawer.Portal>
        <DrawerViewport>
          <DrawerPopup initialFocus={false}>
            {dayDate && (
              <>
                <CloseButton aria-label="Close activity details">×</CloseButton>
                <DrawerHandle
                  type="button"
                  aria-label={
                    mobileSnapPoint === 1
                      ? 'Collapse day details to show more of the map'
                      : 'Expand day details'
                  }
                  onClick={() => setMobileSnapPoint(mobileSnapPoint === 1 ? '13.5rem' : 1)}
                />
                <DrawerContent>
                  <DrawerHeader>
                    <DrawerDate>
                      {restDay ? 'Rest day · ' : ''}
                      {dateFormat.format(new Date(`${dayDate}T12:00:00+12:00`))}
                    </DrawerDate>
                    <DrawerTitle>{dayTitle}</DrawerTitle>
                  </DrawerHeader>

                  {activity && (
                    <>
                      <DayStats>
                        <div>
                          <dt>Distance</dt>
                          <dd>{decimalFormat.format(activity.distance_km)} km</dd>
                        </div>
                        <div>
                          <dt>Climbed</dt>
                          <dd>{numberFormat.format(activity.elevation_gain_m)} m</dd>
                        </div>
                        <div>
                          <dt>Lowest</dt>
                          <dd>
                            {activity.min_elevation_m === null
                              ? '—'
                              : `${numberFormat.format(activity.min_elevation_m)} m`}
                          </dd>
                        </div>
                        <div>
                          <dt>Highest</dt>
                          <dd>
                            {activity.max_elevation_m === null
                              ? '—'
                              : `${numberFormat.format(activity.max_elevation_m)} m`}
                          </dd>
                        </div>
                      </DayStats>

                      <Section>
                        <SectionHeading>
                          <h3>Altitude</h3>
                        </SectionHeading>
                        <ElevationChart
                          activity={activity}
                          highlightedLocation={highlightedPhotoLocation}
                          onLocationChange={onElevationLocationChange}
                        />
                      </Section>
                    </>
                  )}

                  <PhotoSection>
                    <SectionHeading>
                      <h3>Photos</h3>
                    </SectionHeading>
                    <DayPhotos
                      dayDate={dayDate}
                      photos={photos}
                      journeyPhotos={journeyPhotos}
                      spotlightPhotoId={spotlightPhotoId}
                      onActiveDayChange={onSpotlightDayChange}
                      onPhotoHighlightChange={handlePhotoHighlightChange}
                      onSpotlightClose={onSpotlightClose}
                    />
                  </PhotoSection>
                </DrawerContent>
                <DrawerNavigation
                  $collapsed={isMobile && mobileSnapPoint !== 1}
                  aria-label="Browse walk days"
                >
                  <DrawerNavigationButton
                    type="button"
                    disabled={!previousDay}
                    onClick={() => previousDay && onNavigate(previousDay)}
                  >
                    <span>← Previous day</span>
                    <strong>{previousDay?.title ?? 'Start of walk'}</strong>
                  </DrawerNavigationButton>
                  <DrawerNavigationButton
                    type="button"
                    $align="right"
                    disabled={!nextDay}
                    onClick={() => nextDay && onNavigate(nextDay)}
                  >
                    <span>Next day →</span>
                    <strong>{nextDay?.title ?? 'End of walk'}</strong>
                  </DrawerNavigationButton>
                </DrawerNavigation>
              </>
            )}
          </DrawerPopup>
        </DrawerViewport>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
